import { createContext, useEffect, useState } from "react";
import { Board } from "./Board.js";
import { appendToDebugInfo, createLinkElem, getRoomId, getUserId, log, logError, setUserId, SimpleTableBuilder } from "./utils.js";

import P2PNetwork from "./p2p.js";
import useSessionState from "./useSessionState.js";

/** @type P2PNetwork */
let p2p;

// Remove stale session data, if entered new room.
(() => {
    const prevRoomId = sessionStorage.getItem("prevRoomId");
    const currentRoomId = getRoomId();
    let shouldResetSession = !currentRoomId || (prevRoomId !== null && prevRoomId !== currentRoomId);

    if (shouldResetSession) {
        // keep userId
        const userId = getUserId();
        sessionStorage.clear();
        if (userId) setUserId(userId);
    }
})();

/**
 * Value used for automatic negotiation of the player symbol ("X"/"O").
 * @type number | null
 */
let ownDecisionValue = JSON.parse(sessionStorage.getItem("ownDecisionValue") ?? "null");

/**
 * typedef GameContext
 * @property {boolean} gameStarted
 * @property {boolean} thisPlayerIsX 
 */

/**
 * @typedef HistoryJumpPayload
 * @property {number} targetMoveIdx - The move index in the history list to jump to.
 * @property {string[]} squares - The expected resulting board state.
*/

/**
 * @typedef IncomingMessage
 * @property {string} type
 * @property {any} payload
 */

/**
 * @typedef {[IncomingMessage | null, React.Dispatch<React.SetStateAction<IncomingMessage | null>>]}
 * IncomingMessageOrNullReactState
 */

const MessageType = Object.freeze({
    PlayerSymbolNegotiation: "playerSymbolNegotiation",

    /** Payload: a `HistoryItem` object */
    PlayerMove: "playerMove",

    /**
     * Payload:
     * - `targetMoveIdx`: the desired move history index.
     * - `squares`: expected board state for validation.
     */
    JumpInHistory: "jumpInHistory",

    /** Payload: a message for the other player */
    DesyncError: "desyncError",
});

class HistoryItem {
    /**
     * @param {string[]} squares - The gamestate represented as 1D-array.
     * @param {number | null} moveSqIndex - The index inside `squares` where the
     * most recent marker was placed (i.e. the one which lead to the given `squares` state).
     * This field can be `null` in which case there was no previous move.
     */
    constructor(squares, moveSqIndex) {
        this.squares = squares;
        this.moveSqIndex = moveSqIndex;
    }
}

/**
 * Returns whether the given history index represents a state where 'X' will take the next move.
 * @param {number} moveHistoryIndex
 */
function isNextPlayerX(moveHistoryIndex) {
    return moveHistoryIndex % 2 === 0;
}

/**
 * @param {string[]} calculated
 * @param {string[]} received
 */
function validateSquares(calculated, received) {
    return calculated.length === received.length
        && calculated.every((sq, idx) => sq === received[idx]);
}

function generateDecisionValue() {
    const randIntRef = new Uint32Array(1);
    self.crypto.getRandomValues(randIntRef);
    return randIntRef[0];
}

function handleJoinResponse(_response) {
    const roomId = getRoomId();
    if (roomId) sessionStorage.setItem("prevRoomId", roomId);

    // Log session data to debug container
    const table = new SimpleTableBuilder();
    table.addRow("User ID:", getUserId());
    table.addRow("Room ID:", getRoomId());
    appendToDebugInfo(table.build());

    const linkp = document.createElement("p");
    linkp.innerText = "Invite link: ";
    linkp.appendChild(createLinkElem(location.href));
    appendToDebugInfo(linkp);
}

function sendDesyncError() {
    logError("[GameComponent] Desync-Error: Remote and local gamestates mismatch. "
        + "Notifying other player.");
    p2p.send({
        type: MessageType.DesyncError,
        payload: "Desynchronized gamestate detected."
    });
}

/**
 * Access to game data accross components without using module-scoped variables.
 * Introduced to provide a way to access and set who is "player X" during initial
 * negotiations.
*/
export const GameContext = createContext({
    gameStarted: false,
    thisPlayerIsX: false
});

export default function Game() {
    const [gameStarted, setGameStarted] = useSessionState("gs_gameStarted", false);
    const [history, setHistory] = useSessionState("gs_history",
        [new HistoryItem(Array(9).fill(""), null)]);

    const [currentMoveIdx, setCurrentMoveIdx] = useSessionState("gs_currentMoveIdx", 0);
    const xIsNext = isNextPlayerX(currentMoveIdx);
    const currentSquares = history[currentMoveIdx].squares;

    const [thisPlayerIsX, setThisPlayerIsX] = useSessionState("gs_thisPlayerIsX", false);
    const thisPlayerIsNext = thisPlayerIsX === xIsNext;

    // Control ordering of history item list
    const [isMoveOrderDescending, setIsMoveOrderDescending] =
        useSessionState("gs_isMoveOrderDescending", false);

    const [pendingIncomingMessage, setPendingIncomingMessage]
        = /** @type IncomingMessageOrNullReactState */(useState(null));

    // Evaluate incoming message, syncing with local state
    useEffect(() => {
        if (!pendingIncomingMessage) return;
        handleIncomingMessage(pendingIncomingMessage);
        setPendingIncomingMessage(null);
    }, [pendingIncomingMessage]);

    // Setup p2p connection (once)
    useEffect(() => {
        log("[GameComponent] Setting up P2P-connection...");

        const signalingConfig = { handleJoinResponse };
        const envSignalingServer = process.env.REACT_APP_SIGNALING_SERVER;
        if (envSignalingServer) {
            signalingConfig.signalingServer = envSignalingServer;
        }

        const dcConfig = {
            onOpen: () => {
                negotiatePlayerSymbol();
            },
            onMessage: (data) => {
                if (!data || typeof data !== "object" || typeof data.type !== "string") {
                    logError("[GameComponent] Received invalid message format");
                    return;
                }
                // Triggers effect, which evaluates the data on next render
                setPendingIncomingMessage(data);
            },
            onClose: () => { }
        };

        p2p = new P2PNetwork(signalingConfig, dcConfig);
        p2p.initConnection();

        return () => {
            p2p.disconnect();
            log("[GameComponent] Cleaned up P2P-connection");
        };
    }, []);

    function negotiatePlayerSymbol() {
        if (ownDecisionValue !== null) return;
        ownDecisionValue = generateDecisionValue();
        sessionStorage.setItem("ownDecisionValue", JSON.stringify(ownDecisionValue));

        log(`[GameComponent] Sending ownDecisionValue '${ownDecisionValue}' to other player.`);

        p2p.send({
            type: MessageType.PlayerSymbolNegotiation,
            payload: ownDecisionValue,
        });
    }

    /**
     * Evaluates an incoming game message. This function is meant to be called
     * by the effect watching the `pendingIncomingMessage` state variable.
     * @param {IncomingMessage} incomingMessage
     */
    function handleIncomingMessage({ type, payload }) {
        if (type === MessageType.PlayerSymbolNegotiation) {
            // Decide which player is X
            onPlayerSymbolNegotiation(payload);

        } else if (type === MessageType.PlayerMove) {
            if (thisPlayerIsNext) {
                logError(`[GameComponent] Received ${type} but it's my turn. Ignoring it.`);
                return;
            }
            handleRemoteMove(payload);

        } else if (type === MessageType.JumpInHistory) {
            if (thisPlayerIsNext) {
                logError(`[GameComponent] Received ${type} but it's my turn. Ignoring it.`);
                return;
            }
            handleRemoteHistoryJump(payload);

        } else if (type === MessageType.DesyncError) {
            logError("[GameComponent] DesyncError: Opponent claims to have a "
                + "desynchronized gamestate. You might retry your move or restart the game.");
        } else {
            logError(`[GameComponent] Unknwon message type '${type}'.`);
        }
    }

    /** @param {number} peerDecisionValue */
    function onPlayerSymbolNegotiation(peerDecisionValue) {
        log(`[GameComponent] Received peerDecisionValue '${peerDecisionValue}'.`);

        if (ownDecisionValue === null) {
            throw new Error("ownDecisionValue is null");
        }

        if (ownDecisionValue === peerDecisionValue) {
            log(`[GameComponent] ownDecisionValue === peerDecisionValue. Retrying negotiation...`);
            negotiatePlayerSymbol();
            return;
        }

        const nextThisPlayerIsX = ownDecisionValue > peerDecisionValue;
        setThisPlayerIsX(nextThisPlayerIsX);
        setGameStarted(true);

        log(`[GameComponent] Player symbols negotiation result:`);
        const table = new SimpleTableBuilder();
        table.addRow("ownDecisionValue:", ownDecisionValue);
        table.addRow("peerDecisionValue:", peerDecisionValue);
        table.addRow("This player's symbol:", (nextThisPlayerIsX) ? "X" : "O");
        appendToDebugInfo(table.build());
    }

    /** @param {HistoryItem} remoteMove */
    function handleRemoteMove({ squares: receivedSquares, moveSqIndex }) {
        if (moveSqIndex === null) {
            logError(`[GameComponent] Invalid remoteMove (square index '${moveSqIndex}'`);
            return;
        }
        const nextSquares = calculateNextSquares(moveSqIndex);
        if (!validateSquares(nextSquares, receivedSquares)) {
            sendDesyncError();
            return;
        }
        updateGameState(nextSquares, moveSqIndex);
    }

    /** @param {HistoryJumpPayload} historyJumpPayload */
    function handleRemoteHistoryJump({ squares: receivedSquares, targetMoveIdx }) {
        if (!validateSquares(history[targetMoveIdx].squares, receivedSquares)) {
            sendDesyncError();
            return;
        }

        jumpInHistory(targetMoveIdx);
    }

    /**
     * Handles the local player's given move by applying it to the gamestate and
     * then sending an accordung message to the remote player for synchronization.
     * @param {number} moveSqIndex - The index of the recently clicked square.
     */
    function handleLocalPlayerMove(moveSqIndex) {
        const nextSquares = calculateNextSquares(moveSqIndex);
        updateGameState(nextSquares, moveSqIndex);

        log(`[GameComponent] Sending player move '${moveSqIndex}'`);
        p2p.send({
            type: MessageType.PlayerMove,
            payload: new HistoryItem(nextSquares, moveSqIndex),
        });
    }

    /** @param {number} moveSqIndex */
    function calculateNextSquares(moveSqIndex) {
        // Copy current state and update it
        const nextSquares = currentSquares.map(e => e);
        nextSquares[moveSqIndex] = xIsNext ? "X" : "O";
        return nextSquares;
    }

    /**
     * @param {string[]} newSquares - The new board state.
     * @param {number | null} lastSqIndex - Index of most recently added move in `newSquares`.
     */
    function updateGameState(newSquares, lastSqIndex) {
        // Save state to history overwriting any items following the current move index
        const nextHistory = [
            ...history.slice(0, currentMoveIdx + 1),
            new HistoryItem(newSquares, lastSqIndex)
        ];
        setCurrentMoveIdx(nextHistory.length - 1);
        setHistory(nextHistory);
    }

    /** @param {number} histIdx */
    function handleHistoryItemClick(histIdx) {
        if (!thisPlayerIsNext) return;

        jumpInHistory(histIdx);
        p2p.send({
            type: MessageType.JumpInHistory,
            payload: {
                targetMoveIdx: histIdx,
                squares: history[histIdx].squares,
            }
        });
    }

    /** @param {number} targetMoveIdx */
    function jumpInHistory(targetMoveIdx) {
        setCurrentMoveIdx(targetMoveIdx);
    }

    function toggleMoveOrder() {
        setIsMoveOrderDescending(!isMoveOrderDescending);
    }

    // Create move history list
    const moves = history.map((histItem, histIdx) => {
        let description;
        const gridPos = histItem.moveSqIndex != null ?
            [Math.floor(histItem.moveSqIndex / 3) + 1, histItem.moveSqIndex % 3 + 1]
            : [];

        const playerSymbol = !isNextPlayerX(histIdx) ? "X" : "O";
        const moveInfo = `#${histIdx}: ${playerSymbol} (${gridPos[0]}, ${gridPos[1]})`;
        if (histIdx === 0) {
            description = "Go to Start";
        } else {
            description = `Go to move ${moveInfo}`;
        }

        let listItemContent;
        if (histIdx === currentMoveIdx) {
            listItemContent = histIdx > 0 ?
                `Last move ${moveInfo}`
                : "Start";
        } else {
            listItemContent = (
                <button
                    onClick={!thisPlayerIsNext ? () => { } : () => handleHistoryItemClick(histIdx)}
                    disabled={!thisPlayerIsNext}
                >
                    {description}
                </button>
            );
        }

        return (
            <li key={histIdx}>{listItemContent}</li>
        );
    });

    if (isMoveOrderDescending) {
        moves.reverse();
    }

    return (
        <GameContext value={{ gameStarted, thisPlayerIsX }}>
            <div className="game">
                <div>
                    <h4>You are: {!gameStarted ? "..." : thisPlayerIsX ? "X" : "O"}</h4>
                    <Board squares={currentSquares} xIsNext={xIsNext} onPlayerMove={handleLocalPlayerMove} />
                </div>
                <div className="move-history">
                    <h5>Move History</h5>
                    <div>
                        <button onClick={toggleMoveOrder}>Toggle order</button>
                        <ol>{moves}</ol>
                    </div>
                </div>
            </div>
        </GameContext>
    );
}
